import { useEffect, useMemo, useState } from 'react'
import { Button, Loader, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useSearchParams } from 'react-router-dom'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { Patient } from '@medplum/fhirtypes'
import { useOrpc } from '@/hooks/useClient'
import { useSessionState } from '@/hooks/useSessionState'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'
import { FilterHeaderToggle } from '@/components/ui/FilterHeaderToggle'
import { AppointmentDetailsModal } from '@/pages/Appointment/components'
import { BillingModal } from '@/components/billing'
import { BillingSettingsModal } from './components/BillingSettingsModal'
import { BillingTable, type BillingCaseListItem } from './components/BillingTable'
import { FilterPanel } from './components/FilterPanel'
import {
  buildListInput,
  countActiveFilters,
  getDateRangeError,
  persistFilters,
  persistFiltersCollapsed,
  readPersistedFilters,
  readPersistedFiltersCollapsed,
  type BillingV2Filters,
} from './state'

export function BillingV2Page() {
  const orpc = useOrpc()
  const [filters, setFilters] = useState<BillingV2Filters>(() => readPersistedFilters())
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => readPersistedFiltersCollapsed())
  // The patient search narrows the list alongside the filters, which already
  // persist — kept in session memory rather than added to the localStorage
  // filter set, so a patient identifier never reaches disk (PD-1206).
  const [selectedPatient, setSelectedPatient] = useSessionState<Patient | null>(
    'billing:selectedPatient',
    null,
  )
  const [codesItem, setCodesItem] = useState<BillingCaseListItem | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  /** Encounter whose appointment details modal is open — the insurance attach/change flow. */
  const [insuranceEncounterId, setInsuranceEncounterId] = useState<string | null>(null)
  // Drawer state lives in the URL (?drawer={encounterId}) so a refresh reopens
  // it — also the landing target for the legacy page's "Process in V2" link.
  const [, setSearchParams] = useSearchParams()

  const openCase = (item: BillingCaseListItem) => {
    setSearchParams(
      (params) => {
        params.set('drawer', item.encounterId)
        return params
      },
      { replace: true },
    )
  }

  useEffect(() => {
    persistFilters(filters)
  }, [filters])

  const rangeError = getDateRangeError(filters)

  // Legacy-parity infinite scroll: pages accumulate as the table scrolls
  // (react-infinite-scroll-component in BillingTable calls fetchNextPage).
  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useInfiniteQuery(
      orpc.billingV2ListCases.infiniteOptions({
        input: (offset: number) => buildListInput(filters, selectedPatient?.id ?? null, offset),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
          const loaded = allPages.reduce((sum, listPage) => sum + listPage.items.length, 0)
          return loaded < lastPage.total ? loaded : undefined
        },
        enabled: !rangeError,
        placeholderData: keepPreviousData,
      }),
    )

  const items = useMemo(() => (data?.pages ?? []).flatMap((listPage) => listPage.items), [data])
  const total = data?.pages.at(-1)?.total ?? 0

  const queryClient = useQueryClient()
  const syncCase = useMutation(orpc.billingV2SyncCase.mutationOptions())

  // On-demand resync of one case from FHIR — for when the chart already changed
  // and waiting for the next sweep run is not an option.
  const handleSyncCase = async (item: BillingCaseListItem) => {
    try {
      await syncCase.mutateAsync({ encounterId: item.encounterId })
      await queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
    } catch (syncError) {
      showNotification({
        color: 'red',
        message: syncError instanceof Error ? syncError.message : 'Failed to sync the case',
      })
    }
  }

  // Changing filters/patient changes the query key, so the infinite pages reset.
  const handleFiltersChange = setFilters
  const handlePatientChange = setSelectedPatient

  const toggleFiltersCollapsed = () => {
    setFiltersCollapsed((prev) => {
      persistFiltersCollapsed(!prev)
      return !prev
    })
  }

  const activeFilterCount = countActiveFilters(filters)

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-4 p-4">
      <div className="flex shrink-0 gap-4 px-4 py-3">
        <div className="flex flex-col">
          <Text fw={700} size="xl" c="dark">
            Billing
          </Text>
          {data ? (
            <Text size="sm" c="dimmed">
              {items.length.toLocaleString()} of {total.toLocaleString()} cases
            </Text>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PatientSelect
            placeholder="Search patient..."
            setSelectedPatient={handlePatientChange}
            initialPatient={selectedPatient}
            hideAddPatientButton
          />
          <Button variant="default" onClick={() => setSettingsOpen(true)}>
            Billing settings
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-start gap-4">
        <FilterPanel
          className="sticky top-4"
          filters={filters}
          onFiltersChange={handleFiltersChange}
          collapsed={filtersCollapsed}
          onToggleCollapse={toggleFiltersCollapsed}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col self-stretch">
          {isLoading && !rangeError ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white p-8">
              <Loader size="sm" />
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white p-8">
              <Text size="sm" c="red">
                Failed to load billing cases
              </Text>
            </div>
          ) : (
            <BillingTable
              items={items}
              isLoading={isFetching && !isFetchingNextPage}
              hasMore={hasNextPage ?? false}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={() => void fetchNextPage()}
              onOpenCase={openCase}
              onSyncCase={handleSyncCase}
              onAddCodes={setCodesItem}
              onOpenInsurance={(item) => setInsuranceEncounterId(item.encounterId)}
              filterToggle={
                filtersCollapsed ? (
                  <FilterHeaderToggle
                    onShowFilters={toggleFiltersCollapsed}
                    hasActiveFilters={activeFilterCount > 0}
                    activeFilterCount={activeFilterCount}
                  />
                ) : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Insurance attach/change for the visit — the same modal the legacy billing page
          opened from its Insurance Status cell. Mounted only while open: it reads Medplum
          directly, so an always-mounted copy would work on every page render. Assigning a
          Coverage changes the row's insurance columns, hence the refetch. */}
      {insuranceEncounterId ? (
        <AppointmentDetailsModal
          encounterId={insuranceEncounterId}
          onClose={() => setInsuranceEncounterId(null)}
          onDetailsChanged={() => {
            void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
            void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
          }}
        />
      ) : null}
      {codesItem && (
        <BillingModal
          encounterId={codesItem.encounterId}
          opened
          onClose={() => setCodesItem(null)}
          // After the codes save, point-sync the case so the row (codes summary,
          // provider charge) reflects the fresh Claim without waiting for the sweep.
          onSaved={() => handleSyncCase(codesItem)}
        />
      )}
      {settingsOpen ? <BillingSettingsModal onClose={() => setSettingsOpen(false)} /> : null}
    </div>
  )
}
